import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import TopNav from "../components/TopNav";
import StorageBar from "../components/StorageBar";
import { Button } from "../components/ui/button";
import {
  GoogleDriveLogo, CheckCircle, CreditCard, ArrowsClockwise, Coin, X as XIcon,
} from "@phosphor-icons/react";

function loadRazorpayScript() {
  return new Promise((resolve) => {
    if (window.Razorpay) return resolve(true);
    const s = document.createElement("script");
    s.src = "https://checkout.razorpay.com/v1/checkout.js";
    s.onload = () => resolve(true);
    s.onerror = () => resolve(false);
    document.body.appendChild(s);
  });
}

const STATUS_LABEL = {
  active: "Active", authenticated: "Awaiting first charge",
  created: "Pending activation", pending: "Pending", paused: "Paused",
  halted: "Halted", cancelled: "Cancelled", completed: "Ended",
};

export default function Storage() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [packages, setPackages] = useState({});
  const [subs, setSubs] = useState([]);
  const [busy, setBusy] = useState(null);
  const [provider, setProvider] = useState("razorpay"); // razorpay | stripe
  const [mode, setMode] = useState("subscription"); // subscription | onetime

  const load = async () => {
    try {
      const me = await api.get("/auth/me");
      setUser(me.data);
      const p = await api.get("/payments/packages");
      setPackages(p.data);
      const s = await api.get("/subscriptions");
      setSubs(s.data);
    } catch {
      navigate("/", { replace: true });
    }
  };

  useEffect(() => { load(); }, []);

  const buyStripeOnce = async (pkgId) => {
    const r = await api.post("/payments/checkout", {
      package_id: pkgId, origin_url: window.location.origin,
    });
    window.location.href = r.data.checkout_url;
  };

  const buyStripeSub = async (pkgId) => {
    const r = await api.post("/payments/stripe/subscribe", {
      package_id: pkgId, origin_url: window.location.origin,
    });
    window.location.href = r.data.checkout_url;
  };

  const buyRazorpayOnce = async (pkgId) => {
    const ok = await loadRazorpayScript();
    if (!ok) throw new Error("Razorpay failed to load");
    const r = await api.post("/payments/razorpay/order", { package_id: pkgId });
    const order = r.data;
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: order.key_id, amount: order.amount, currency: order.currency,
        name: "Antic Diary", description: order.package_name,
        order_id: order.order_id,
        prefill: { name: order.user_name, email: order.user_email },
        theme: { color: "#3B5249" },
        handler: async (resp) => {
          try {
            await api.post("/payments/razorpay/verify", {
              razorpay_order_id: resp.razorpay_order_id,
              razorpay_payment_id: resp.razorpay_payment_id,
              razorpay_signature: resp.razorpay_signature,
            });
            navigate(`/payment/success?session_id=${resp.razorpay_order_id}&provider=razorpay`);
            resolve();
          } catch (e) { reject(e); }
        },
        modal: { ondismiss: () => reject(new Error("dismissed")) },
      });
      rzp.on("payment.failed", () => reject(new Error("failed")));
      rzp.open();
    });
  };

  const buyRazorpaySub = async (pkgId) => {
    const ok = await loadRazorpayScript();
    if (!ok) throw new Error("Razorpay failed to load");
    const r = await api.post("/payments/razorpay/subscribe", { package_id: pkgId });
    const sub = r.data;
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        key: sub.key_id, subscription_id: sub.subscription_id,
        name: "Antic Diary", description: `${sub.package_name} — Monthly`,
        prefill: { name: sub.user_name, email: sub.user_email },
        theme: { color: "#3B5249" },
        recurring: true,
        handler: async () => {
          navigate(`/payment/success?session_id=${sub.subscription_id}&provider=razorpay&type=subscription`);
          resolve();
        },
        modal: { ondismiss: () => reject(new Error("dismissed")) },
      });
      rzp.on("payment.failed", () => reject(new Error("failed")));
      rzp.open();
    });
  };

  const buy = async (pkgId) => {
    setBusy(pkgId);
    try {
      if (provider === "stripe" && mode === "onetime") await buyStripeOnce(pkgId);
      else if (provider === "stripe" && mode === "subscription") await buyStripeSub(pkgId);
      else if (provider === "razorpay" && mode === "onetime") await buyRazorpayOnce(pkgId);
      else await buyRazorpaySub(pkgId);
    } catch (e) {
      if (e?.message !== "dismissed") {
        toast.error(e?.response?.data?.detail || e?.message || "Failed");
      }
      setBusy(null);
    }
  };

  const cancelSub = async (sub) => {
    if (!confirm(`Cancel subscription? Storage stays active until ${sub.current_period_end ? new Date(sub.current_period_end).toLocaleDateString() : "end of billing period"}.`)) return;
    try {
      await api.post(`/subscriptions/${sub.sub_id}/cancel`);
      toast.success("Subscription cancelled. Storage remains until period end.");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    }
  };

  const connectDrive = async () => {
    try {
      const r = await api.get("/drive/connect");
      window.location.href = r.data.authorization_url;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Google Drive not set up on this server yet.");
    }
  };

  const disconnectDrive = async () => {
    await api.post("/drive/disconnect");
    toast.success("Disconnected");
    load();
  };

  if (!user) return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Loading…</div>;

  const activeSubs = subs.filter((s) => ["active", "authenticated", "created", "pending"].includes(s.status));

  return (
    <div className="min-h-screen paper-grain" data-testid="storage-page">
      <TopNav user={user} onLogout={async () => { await api.post("/auth/logout"); navigate("/"); }} />

      <main className="max-w-4xl mx-auto px-6 py-12">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">Storage</p>
        <h1 className="font-serif text-5xl lg:text-6xl font-light tracking-tighter mt-3 mb-10">
          Room to <span className="italic text-accent">breathe</span>
        </h1>

        <StorageBar user={user} />

        {/* Active subscriptions */}
        {activeSubs.length > 0 && (
          <section className="mt-12" data-testid="active-subs-section">
            <h2 className="font-serif text-3xl mb-4">Your active plans</h2>
            <ul className="space-y-3">
              {activeSubs.map((s) => (
                <li
                  key={s.sub_id}
                  data-testid={`sub-${s.sub_id}`}
                  className="flex items-center justify-between border border-border rounded-lg bg-card p-5"
                >
                  <div>
                    <div className="flex items-center gap-3">
                      <ArrowsClockwise size={18} weight="duotone" className="text-primary" />
                      <span className="font-serif text-xl">{s.gb} GB · ₹{s.amount.toFixed(0)}/mo</span>
                      <span className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                        · {s.provider}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">
                      {STATUS_LABEL[s.status] || s.status}
                      {s.current_period_end && ` · renews ${new Date(s.current_period_end).toLocaleDateString()}`}
                      {s.cancel_at_cycle_end && " · cancels at period end"}
                    </p>
                  </div>
                  {!s.cancel_at_cycle_end && (
                    <Button
                      data-testid={`cancel-sub-${s.sub_id}`}
                      variant="ghost"
                      size="sm"
                      onClick={() => cancelSub(s)}
                      className="text-xs uppercase tracking-[0.2em] text-muted-foreground hover:text-destructive"
                    >
                      <XIcon size={14} className="mr-1" /> Cancel
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          </section>
        )}

        {/* Google Drive */}
        <section className="mt-16">
          <h2 className="font-serif text-3xl mb-4">Unlimited via Google Drive</h2>
          <p className="text-sm text-muted-foreground leading-relaxed mb-6 max-w-xl">
            Route all uploads directly to your own Google Drive. Your diary metadata stays with us; the media lives with you. No storage cap.
          </p>
          {user.drive_connected ? (
            <div className="flex items-center gap-4">
              <span
                data-testid="drive-status-connected"
                className="flex items-center gap-2 text-xs uppercase tracking-[0.2em] text-primary"
              >
                <CheckCircle size={18} weight="duotone" /> Drive connected
              </span>
              <Button
                data-testid="disconnect-drive-btn"
                variant="ghost"
                onClick={disconnectDrive}
                className="text-xs uppercase tracking-[0.2em]"
              >
                Disconnect
              </Button>
            </div>
          ) : (
            <Button
              data-testid="connect-drive-btn"
              onClick={connectDrive}
              className="rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
            >
              <GoogleDriveLogo size={18} weight="duotone" className="mr-2" />
              Connect Google Drive
            </Button>
          )}
        </section>

        {/* Paid plans */}
        <section className="mt-16">
          <h2 className="font-serif text-3xl mb-4">Or buy extra storage</h2>
          <p className="text-sm text-muted-foreground mb-6 max-w-xl">
            ₹100 per GB per month. Cancel anytime.
          </p>

          {/* Mode toggle */}
          <div className="flex flex-wrap gap-4 items-center mb-8">
            <div
              className="inline-flex items-center gap-1 p-1 bg-secondary rounded-full border border-border"
              data-testid="mode-toggle"
            >
              <button
                data-testid="mode-subscription"
                onClick={() => setMode("subscription")}
                className={`px-5 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-full transition-colors flex items-center gap-2 ${
                  mode === "subscription" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <ArrowsClockwise size={14} /> Monthly auto-renew
              </button>
              <button
                data-testid="mode-onetime"
                onClick={() => setMode("onetime")}
                className={`px-5 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-full transition-colors flex items-center gap-2 ${
                  mode === "onetime" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                <Coin size={14} /> One-time
              </button>
            </div>

            <div
              className="inline-flex items-center gap-1 p-1 bg-secondary rounded-full border border-border"
              data-testid="provider-toggle"
            >
              <button
                data-testid="provider-razorpay"
                onClick={() => setProvider("razorpay")}
                className={`px-5 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-full transition-colors ${
                  provider === "razorpay" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Razorpay
              </button>
              <button
                data-testid="provider-stripe"
                onClick={() => setProvider("stripe")}
                className={`px-5 py-2 text-xs uppercase tracking-[0.2em] font-semibold rounded-full transition-colors ${
                  provider === "stripe" ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"
                }`}
              >
                Stripe
              </button>
            </div>
          </div>

          <div className="grid sm:grid-cols-3 gap-4" data-testid="packages-grid">
            {Object.entries(packages).map(([id, p]) => (
              <div key={id} className="border border-border rounded-lg bg-card p-6 flex flex-col">
                <p className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                  {mode === "subscription" ? "Monthly" : "One-time"}
                </p>
                <p className="font-serif text-4xl mt-2">{p.gb} GB</p>
                <p className="text-sm text-muted-foreground mt-1">
                  {mode === "subscription" ? "per month, auto-renew" : "for 1 month"}
                </p>
                <div className="mt-6 font-serif text-3xl">₹{p.amount.toFixed(0)}</div>
                <Button
                  data-testid={`buy-${id}`}
                  onClick={() => buy(id)}
                  disabled={busy === id}
                  className="mt-auto pt-4 rounded-full bg-primary text-primary-foreground hover:bg-primary/90"
                >
                  <CreditCard size={14} weight="duotone" className="mr-2" />
                  {busy === id
                    ? "Processing…"
                    : mode === "subscription"
                      ? `Subscribe via ${provider === "stripe" ? "Stripe" : "Razorpay"}`
                      : `Pay via ${provider === "stripe" ? "Stripe" : "Razorpay"}`}
                </Button>
              </div>
            ))}
          </div>
          <p className="mt-6 text-xs text-muted-foreground">
            {provider === "razorpay"
              ? "Razorpay LIVE mode — real payments captured. UPI, cards, netbanking, and auto-mandate for monthly."
              : "Stripe test mode — use card 4242 4242 4242 4242, any future expiry, any CVC."}
          </p>
        </section>
      </main>
    </div>
  );
}
